// Discovers the symbology an ArcGIS Online item author saved for a hosted Feature Service
// sublayer. Map Viewer's "nice" rendering of a hosted service comes from its registered item
// (the service metadata's serviceItemId), whose /data resource carries per-layer
// layerDefinition.drawingInfo overrides — the service's own drawingInfo is often just a flat
// default. Consumers (arcgis-feature-layer.mjs, arcgis-legend.mjs) prefer the override when one
// exists. Everything here fails soft: no item, a private item, a non-arcgis.com host, or any
// fetch/parse failure resolves to null and the service symbology applies unchanged.

// ArcGIS Online item ids are 32-char hex; be lenient on the alphabet but strict on shape so the
// id can be embedded in a URL without further encoding.
const ITEM_ID_RE = /^[a-z0-9]{32}$/i;

// The sharing-API data URL for a service's registered item, or null when the item id is
// malformed or the service is not ArcGIS Online-hosted (Enterprise portals are not derivable
// from a service URL, so only *.arcgis.com services are probed — and only over https).
export function arcgisItemDataUrl(serviceItemId, serviceUrl) {
	if (typeof serviceItemId !== "string" || !ITEM_ID_RE.test(serviceItemId)) {
		return null;
	}
	if (typeof serviceUrl !== "string") {
		return null;
	}
	let parsed = null;
	try {
		parsed = new URL(serviceUrl);
	} catch (error) {
		return null;
	}
	if (parsed.protocol !== "https:") {
		return null;
	}
	const host = parsed.hostname.toLowerCase();
	if (host !== "arcgis.com" && !host.endsWith(".arcgis.com")) {
		return null;
	}
	return `https://www.arcgis.com/sharing/rest/content/items/${serviceItemId}/data?f=json`;
}

// The item /data entry for one sublayer, or null. Shared by the drawingInfo and popupInfo
// extractors; requires a real id — Number("")/Number(null) coerce to 0 and would silently
// match sublayer 0.
function itemLayerEntry(itemData, sublayerId) {
	if (itemData == null || typeof itemData !== "object" || !Array.isArray(itemData.layers)) {
		return null;
	}
	if (sublayerId == null || (typeof sublayerId === "string" && sublayerId.trim() === "")) {
		return null;
	}
	const wanted = Number(sublayerId);
	if (!Number.isFinite(wanted)) {
		return null;
	}
	for (const entry of itemData.layers) {
		if (entry != null && Number(entry.id) === wanted) {
			return entry;
		}
	}
	return null;
}

// The drawingInfo override the item stores for one sublayer, or null. Requires a renderer:
// an override that only sets transparency/labeling must not replace the service renderer.
export function itemOverrideDrawingInfo(itemData, sublayerId) {
	const entry = itemLayerEntry(itemData, sublayerId);
	if (entry != null
		&& entry.layerDefinition != null
		&& entry.layerDefinition.drawingInfo != null
		&& entry.layerDefinition.drawingInfo.renderer != null) {
		return entry.layerDefinition.drawingInfo;
	}
	return null;
}

// The popupInfo the item stores for one sublayer (authored popup title, field visibility,
// labels, and formats), or null.
export function itemPopupInfo(itemData, sublayerId) {
	const entry = itemLayerEntry(itemData, sublayerId);
	if (entry != null && entry.popupInfo != null && typeof entry.popupInfo === "object") {
		return entry.popupInfo;
	}
	return null;
}

// Session cache of item /data documents keyed by data URL. The PROMISE is cached (not the
// resolved value) so concurrent sublayers of the same service share a single request; failures
// resolve (never reject). A definitive sharing-API answer — HTTP 200 with an {error} body for
// private/missing items — caches null, so those are probed once per page; a transient failure
// (network blip, 5xx) evicts its entry so the next request retries.
const itemDataCache = new Map();

function fetchItemLayerData(serviceItemId, serviceUrl) {
	const url = arcgisItemDataUrl(serviceItemId, serviceUrl);
	if (url == null) {
		return Promise.resolve(null);
	}
	let promise = itemDataCache.get(url);
	if (promise == null) {
		promise = fetch(url, { headers: { Accept: "application/json" } })
			.then((response) => {
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}`);
				}
				return response.json();
			})
			// The sharing API answers HTTP 200 with an {error} body for private/missing items.
			.then((json) => (json != null && typeof json === "object" && json.error == null ? json : null))
			.catch(() => {
				itemDataCache.delete(url);
				return null;
			});
		itemDataCache.set(url, promise);
	}
	return promise;
}

export function fetchItemOverrideDrawingInfo(serviceItemId, serviceUrl, sublayerId) {
	return fetchItemLayerData(serviceItemId, serviceUrl).then((itemData) => itemOverrideDrawingInfo(itemData, sublayerId));
}

// Both per-sublayer overrides in one resolve (they live in the same cached document, so this
// costs nothing over fetching either alone). Never rejects; each half is independently null.
export function fetchItemLayerOverrides(serviceItemId, serviceUrl, sublayerId) {
	return fetchItemLayerData(serviceItemId, serviceUrl).then((itemData) => ({
		drawingInfo: itemOverrideDrawingInfo(itemData, sublayerId),
		popupInfo: itemPopupInfo(itemData, sublayerId)
	}));
}
